import { useFadeIn } from '@/hooks/useFadeIn';

interface FlowStepProps {
  step: number;
  icon: string;
  title: string;
  time: string;
  desc: string;
  delay: number;
}

function FlowStep({ step, icon, title, time, desc, delay }: FlowStepProps) {
  const fade = useFadeIn({ direction: 'up', delay });
  return (
    <div
      ref={fade.ref}
      className="bg-white rounded-lg border-t-2 p-6 md:p-8 w-full flex-1"
      style={{ borderTopColor: '#3E5F8F', ...fade.style }}
    >
      <div className="flex items-center justify-center mb-4">
        <span
          className="inline-flex items-center justify-center w-8 h-8 rounded-full text-white font-medium text-sm"
          style={{ backgroundColor: '#3E5F8F' }}
        >
          {step}
        </span>
      </div>
      <div className="mb-4 flex justify-center">
        <div className="w-12 h-12 flex items-center justify-center">
          <i className={`${icon} text-2xl`} style={{ color: '#3E5F8F' }}></i>
        </div>
      </div>
      <h3 className="font-serif text-gray-900 text-base md:text-lg text-center leading-relaxed mb-2">
        {title}
      </h3>
      <span className="block text-center font-medium text-sm mb-3" style={{ color: '#3E5F8F' }}>
        {time}
      </span>
      <p className="font-light text-gray-700 text-sm text-center leading-loose">{desc}</p>
    </div>
  );
}

const flowSteps = [
  {
    icon: 'ri-drop-line',
    title: '専用ジェルを塗布',
    time: '約15〜30分',
    desc: '特殊なジェルで歯の表面を整えます。数回くり返すことで樹脂が入りやすい状態にします。',
  },
  {
    icon: 'ri-ink-bottle-line',
    title: '樹脂を浸透させる',
    time: '約5〜10分',
    desc: '透明な樹脂（インフィルトラント）を歯の内部へ浸透させます。光照射で固めます。',
  },
  {
    icon: 'ri-sparkling-line',
    title: '最終研磨・仕上げ',
    time: '約5〜10分',
    desc: '歯の表面を丁寧に研磨して、自然な光沢と滑らかさを整えて完成です。',
  },
];

export default function IconFlow() {
  const heading = useFadeIn({ direction: 'up' });
  const note = useFadeIn({ direction: 'up', delay: 400 });

  return (
    <section id="steps" className="bg-gray-50 py-14 md:py-20">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div ref={heading.ref} style={heading.style} className="text-center">
          <span
            className="inline-block font-medium text-xs md:text-sm tracking-[0.2em]"
            style={{ color: '#3E5F8F' }}
          >
            Treatment Steps
          </span>
          <div className="w-8 h-px mt-3 mb-4 mx-auto" style={{ backgroundColor: '#3E5F8F' }}></div>
          <h2 className="font-serif text-gray-900 text-2xl md:text-3xl leading-relaxed mb-4">
            治療当日の流れ
          </h2>
          <p className="font-light text-gray-500 text-sm md:text-base leading-loose">
            すべて1回の通院で完了します。
          </p>
        </div>

        {/* デスクトップ */}
        <div className="hidden md:flex items-stretch justify-center gap-0 mt-10 md:mt-12">
          {flowSteps.map((s, i) => (
            <div key={s.title} className="flex items-center">
              <div className="w-56 lg:w-64">
                <FlowStep
                  step={i + 1}
                  icon={s.icon}
                  title={s.title}
                  time={s.time}
                  desc={s.desc}
                  delay={i * 100}
                />
              </div>
              {i < flowSteps.length - 1 && (
                <div className="flex items-center justify-center w-12 flex-shrink-0">
                  <i className="ri-arrow-right-line text-xl text-gray-300"></i>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* モバイル */}
        <div className="flex md:hidden flex-col gap-0 mt-10">
          {flowSteps.map((s, i) => (
            <div key={s.title} className="w-full">
              <FlowStep
                step={i + 1}
                icon={s.icon}
                title={s.title}
                time={s.time}
                desc={s.desc}
                delay={i * 100}
              />
              {i < flowSteps.length - 1 && (
                <div className="flex items-center justify-center py-3">
                  <i className="ri-arrow-down-line text-xl text-gray-300"></i>
                </div>
              )}
            </div>
          ))}
        </div>

        <div ref={note.ref} style={note.style} className="mt-10 md:mt-12">
          <div
            className="rounded-lg p-5 md:p-6 max-w-2xl mx-auto bg-gray-50"
            style={{ borderLeft: '2px solid #3E5F8F' }}
          >
            <p className="font-light text-gray-500 text-sm leading-loose">
              <span className="font-medium text-gray-700">所要時間の目安：約30〜60分</span><br />
              白斑の範囲・深さによって異なります。
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}